import React, {
	useCallback,
	useEffect,
	useState,
	useMemo,
	FocusEvent,
	SyntheticEvent,
} from 'react';
import { AutocompleteValue, Box, SxProps, TextFieldProps, Theme, Typography, useAutocomplete } from '@mui/material';
import { isEqual, uniqBy } from 'lodash-es';
import { CoreFormControlProps } from '../_core/core-form-control';
import { CoreAutocomplete } from './autocomplete.component';
import { useDebounce } from '../../hooks';
import { PlacesAutocompleteOption } from '../../interfaces';
import { Colors } from '../../constants';
import { defaultAutoCompleteStyles } from '../inputs/input.styles';
import { clsx } from '../../utils';
import { CloseIcon } from '../../icons';
import { getPlaceAutocompleteOptions, GetPlacesCallback } from '../../utils/places-autocomplete.utils';

export interface PlacesAutocompleteProps<Multiple extends boolean | undefined = undefined>
	extends CoreFormControlProps {
	values?: AutocompleteValue<PlacesAutocompleteOption, Multiple, boolean | undefined, undefined>;
	placeholder?: string;
	sx?: SxProps<Theme>;
	getPlaceOptions: GetPlacesCallback;
	hideOptions?: boolean;
	freeSolo?: boolean;
	multiple?: Multiple;
	textFieldProps?: TextFieldProps;
	onInputChange?(value: string): void;
	onPlaceInputFocus?: (e: SyntheticEvent) => void;
	onSelect?(
		values: Multiple extends true ? (string | PlacesAutocompleteOption)[] : PlacesAutocompleteOption | null
	): void;
	onBlur?(e: FocusEvent): void;
}

export const PlacesAutocomplete = <Multiple extends boolean | undefined = true>({
	values,
	multiple = true as Multiple,
	placeholder,
	onSelect,
	onBlur,
	getPlaceOptions,
	onPlaceInputFocus,
	onInputChange,
	hideOptions,
	freeSolo,
	...autocompleteProps
}: PlacesAutocompleteProps<Multiple>) => {
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState<PlacesAutocompleteOption[]>([]);
	const { debounceFunc } = useDebounce(400);

	const defaultOption = useMemo(
		(): PlacesAutocompleteOption => ({
			id: 'default-option',
			description: inputValue,
			formattedSuggestion: {
				mainText: inputValue,
			},
		}),
		[inputValue]
	);

	const loadPlaces = useCallback((): void => {
		if (!inputValue) {
			setOptions([]);

			return;
		}

		debounceFunc(async (): Promise<void> => {
			const newOptions = await getPlaceAutocompleteOptions(inputValue, getPlaceOptions, defaultOption);

			setOptions(newOptions);
		});
	}, [debounceFunc, inputValue, defaultOption, getPlaceOptions]);

	useEffect(() => {
		loadPlaces();
	}, [loadPlaces]);

	useEffect(() => {
		// setInputValue('');
	}, [values]);

	const onSuggestionSelected = useCallback(
		(
			suggestions: Multiple extends true ? (string | PlacesAutocompleteOption)[] : PlacesAutocompleteOption | null
		): void => {
			onSelect?.(suggestions);
		},
		[onSelect]
	);

	const onPlaceInputChange = useCallback(
		(value: string): void => {
			setInputValue(value);
			onInputChange?.(value);
		},
		[onInputChange]
	);

	// const removeItem = useCallback(
	// 	(id: string) => {
	// 		const newValues = [...(values ?? [])].filter((val) => val.id !== id);

	// 		onSuggestionSelected(newValues);
	// 	},
	// 	[values]
	// );

	return (
		<CoreAutocomplete
			{...autocompleteProps}
			textFieldProps={{
				...autocompleteProps.textFieldProps,
				InputProps: {
					...autocompleteProps.textFieldProps?.InputProps,
					endAdornment: null,
				},
			}}
			multiple={multiple}
			sx={clsx(defaultAutoCompleteStyles(Array.isArray(values) ? !!values?.length : !!values), autocompleteProps.sx)}
			value={values}
			options={hideOptions ? [] : options}
			onBlur={onBlur}
			forcePopupIcon={false}
			clearIcon={null}
			isOptionEqualToValue={(option, value) => isEqual(option, value)}
			filterOptions={options => options}
			getOptionDisabled={option => option.id === 'default-option' || false}
			onChange={(event, suggestions) => {
			onSuggestionSelected(
				suggestions as Multiple extends true ? (string | PlacesAutocompleteOption)[] : PlacesAutocompleteOption | null
				);
			}}
			inputValue={inputValue}
			onInputChange={(e, value) => {
				onPlaceInputChange(value);
			}}
			freeSolo={freeSolo as any}
			onFocus={onPlaceInputFocus}
			placeholder={placeholder}
			renderTags={(values, getTagProps) => (
				<Box
					sx={{
						display: 'flex',
						flexFlow: 'row wrap',
						gap: '5px',
					}}
				>
					{values?.map((value, index) => {
						const { onDelete, ...props } = getTagProps({ index });

						return (
							<Box
								{...props}
								sx={{
									cursor: 'default',
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									backgroundColor: Colors.white_a5,
									borderRadius: '5px',
									px: '10px',
									gap: '6px',
								}}
							>
								<Typography
									sx={{
										fontFamily: 'Montserrat',
									}}
								>
									{value.description}
								</Typography>

								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										cursor: 'pointer',
									}}
									onClick={onDelete}
								>
									<CloseIcon width={18} height={18} />
								</Box>
							</Box>
						);
					})}
				</Box>
			)}
			getOptionKey={option => (typeof option === 'string' ? option : option.id)}
			getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
			renderOption={(props, { formattedSuggestion }) => (
				<Box
					{...props}
					component='li'
					sx={{
						'&:hover': {
							backgroundColor: Colors.white_a10,
						},
					}}
				>
					<Box>
						<Typography
							sx={{
								color: Colors.white,
							}}
						>
							{formattedSuggestion.mainText}
						</Typography>
						<Typography
							sx={{
								fontSize: '12px',
								color: Colors.gray,
							}}
						>
							{formattedSuggestion.secondaryText}
						</Typography>
					</Box>
				</Box>
			)}
		/>
	);
};
